import { MoreHorizontal } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { useToast } from "@/hooks/use-toast";
import { formatPhoneNumber } from "@/utils/phoneFormatter";

interface ContactRowMenuProps {
  name: string;
  phone: string;
  onViewDetails: () => void;
  /** Whether the owner already turned off marketing texts for this customer. */
  marketingExcluded?: boolean;
  /** Opens the exclusion confirmation; omitted while exclusions are unavailable. */
  onToggleMarketing?: () => void;
}

/**
 * The per-row menu: view, copy, call, and "Don't send marketing texts" (or its
 * undo). The browser never writes sms_contacts itself: the exclusion goes
 * through a confirmation and then the sms-actions edge function.
 */
export function ContactRowMenu({ name, phone, onViewDetails, marketingExcluded = false, onToggleMarketing }: ContactRowMenuProps) {
  const { toast } = useToast();
  const formatted = formatPhoneNumber(phone);

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(formatted);
      toast({ title: "Phone number copied" });
    } catch {
      toast({ title: "Couldn't copy the number", description: formatted, variant: "destructive" });
    }
  };

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          variant="ghost"
          size="icon"
          className="h-11 w-11 shrink-0 text-muted-foreground"
          aria-label={`Actions for ${name}`}
          onClick={(e) => e.stopPropagation()}
        >
          <MoreHorizontal className="h-5 w-5" aria-hidden="true" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-56" onClick={(e) => e.stopPropagation()}>
        <DropdownMenuItem className="min-h-11" onSelect={onViewDetails}>
          View details and history
        </DropdownMenuItem>
        <DropdownMenuItem className="min-h-11" onSelect={copy}>
          Copy phone number
        </DropdownMenuItem>
        <DropdownMenuItem className="min-h-11" asChild>
          <a href={`tel:+1${phone}`}>Call {formatted}</a>
        </DropdownMenuItem>
        {onToggleMarketing && (
          <>
            <DropdownMenuSeparator />
            <DropdownMenuItem className="min-h-11" onSelect={onToggleMarketing}>
              {marketingExcluded ? "Allow marketing texts again" : "Don't send marketing texts"}
            </DropdownMenuItem>
          </>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
